import React from 'react';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH, ScreenName } from '@helpcenter/constants';
import {
    isHideVsaEndpointsEnabled,
    isVsaAdoptionHeaderChatButtonEnabled,
    isVsaAdoptionPortalChatSideBarEnabled,
} from '@helpcenter/feature-flags';
import { getHelpCenterAri, getCloudId } from '@helpcenter/meta';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { usePathParam, useRouter } from 'react-resource-router';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AiIcon from '@atlaskit/icon/core/ai-agent';
import { token } from '@atlaskit/tokens';

import { UI_EVENT_TYPE, ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri } from '@atlassian/ari';

import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { history } from '@atlassian/help-center-common-util/history';
import { useVirtualAgentAvailability } from '@atlassian/help-center-virtual-agent/src/services/virtual-agent-availability';
import { DynamicThemingButton } from '../dynamic-theming-button';
import type { virtualServiceAgentHeaderDumbQuery } from './__generated__/virtualServiceAgentHeaderDumbQuery.graphql';
import messages from './messages';

export interface ChatSideBarProps {
    isChatSideBarOpen?: boolean;
    openChatSideBar?: () => void;
    closeChatSideBar?: () => void;
}

export interface Props extends ChatSideBarProps {
    isJiraAdmin: boolean;
    projectId?: number;
    isProjectAdmin?: boolean;
}

const VirtualServiceAgentButton = ({
    onClick,
    label,
    isChatSideBarOpen,
}: {
    onClick: () => void;
    label: string;
    isChatSideBarOpen?: boolean;
}) => {
    const { formatMessage } = useIntl();
    return (
        <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
            <DynamicThemingButton
                customStyles={
                    isChatSideBarOpen
                        ? {
                              borderColor: token('color.border.focused', '#0C66E4'),
                              borderWidth: token('border.width', '1px'),
                              color: token('color.text.accent.blue', '#0C66E4'), // this is for the icon color
                          }
                        : {}
                }
                label={label}
                textColor={isChatSideBarOpen ? 'color.text.accent.blue' : undefined} // this is for the text color
                icon={<AiIcon label={formatMessage(messages.virtualServiceAgent)} />}
                onClick={onClick}
            />
            <FireScreenEventOnMount />
        </ContextualAnalyticsData>
    );
};

const VirtualServiceAgentHeaderForPortal = ({
    cloudId,
    portalId,
    projectId,
    isChatSideBarOpen,
    openChatSideBar,
    closeChatSideBar,
    isProjectAdmin,
}: {
    cloudId: string;
    portalId: string;
    projectId: number;
    isChatSideBarOpen?: boolean;
    openChatSideBar?: () => void;
    closeChatSideBar?: () => void;
    isProjectAdmin?: boolean;
}): React.ReactNode => {
    di(
        isVsaAdoptionHeaderChatButtonEnabled,
        useVirtualAgentAvailability,
        isVsaAdoptionPortalChatSideBarEnabled,
        useRouter
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const [{ route }] = useRouter();
    const { isVirtualAgentEnabled: isVirtualAgentAvailableForProject } = useVirtualAgentAvailability({
        cloudId,
        projectId,
        isLoggedIn: true, // this component has a check to only show for logged in users
    });
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentChatButton',
            attributes: {
                source: 'portal',
            },
        }).fire();
        if (isVsaAdoptionPortalChatSideBarEnabled() && route.name === 'portal-home' && !isProjectAdmin) {
            if (isChatSideBarOpen) {
                closeChatSideBar?.();
            } else {
                openChatSideBar?.();
            }
        } else {
            history.push(`/portal/${portalId}/conversation`);
        }
    };
    if (isVirtualAgentAvailableForProject && isVsaAdoptionHeaderChatButtonEnabled()) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton
                    onClick={handleVirtualAgentMenuClick}
                    label={formatMessage(messages.chat)}
                    isChatSideBarOpen={isChatSideBarOpen}
                />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderForHelpCenter = ({ cloudId }: { cloudId: string }) => {
    di(isVsaAdoptionHeaderChatButtonEnabled);
    const helpCenterAri = getHelpCenterAri();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const virtualAgentEnabledData = useLazyLoadQuery<virtualServiceAgentHeaderDumbQuery>(
        graphql`
            query virtualServiceAgentHeaderDumbHelpCenterQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        __typename
                        ... on HelpCenter {
                            virtualAgentEnabled
                        }
                    }
                }
                virtualAgent {
                    virtualAgentAvailability(containerId: $helpCenterAri)
                }
            }
        `,
        {
            cloudId,
            helpCenterAri,
        }
    );
    const isVirtualAgentEnabled =
        virtualAgentEnabledData?.helpCenter?.helpCenterById?.__typename === 'HelpCenter' &&
        virtualAgentEnabledData.helpCenter.helpCenterById.virtualAgentEnabled;

    const isVirtualAgentAvailable = virtualAgentEnabledData?.virtualAgent?.virtualAgentAvailability ?? false;
    const isHelpSeekerWithVSAEnabled =
        isVsaAdoptionHeaderChatButtonEnabled() && isVirtualAgentAvailable && isVirtualAgentEnabled;
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentChatButton',
            attributes: {
                source: 'help-center',
            },
        }).fire();
        history.push(`/conversation`);
    };

    if (isHelpSeekerWithVSAEnabled) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton onClick={handleVirtualAgentMenuClick} label={formatMessage(messages.chat)} />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderForAdmin = ({ cloudId, isJiraAdmin }: { cloudId: string; isJiraAdmin: boolean }) => {
    const helpCenterAri = getHelpCenterAri();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const virtualAgentEnabledData = useLazyLoadQuery<virtualServiceAgentHeaderDumbQuery>(
        graphql`
            query virtualServiceAgentHeaderDumbAdminQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        __typename
                        ... on HelpCenter {
                            virtualAgentEnabled
                        }
                    }
                }
            }
        `,
        {
            cloudId,
            helpCenterAri,
        }
    );
    const isVirtualAgentEnabled =
        virtualAgentEnabledData?.helpCenter?.helpCenterById?.__typename === 'HelpCenter' &&
        virtualAgentEnabledData.helpCenter.helpCenterById.virtualAgentEnabled;
    const adminWithoutVSAEnabled = isJiraAdmin && !isVirtualAgentEnabled;
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentButton',
            attributes: {
                source: 'admin',
            },
        }).fire();
        const { helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
        window.location.href = `${window.location.origin}${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/virtual-agent`;
    };
    if (adminWithoutVSAEnabled) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton
                    onClick={handleVirtualAgentMenuClick}
                    label={formatMessage(messages.virtualServiceAgent)}
                />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderDumbWrapper = ({
    isJiraAdmin,
    projectId,
    isChatSideBarOpen,
    openChatSideBar,
    closeChatSideBar,
    isProjectAdmin,
}: Props) => {
    di(getCloudId);
    di(isHideVsaEndpointsEnabled);
    di(usePathParam);
    const cloudId = getCloudId();
    const [portalId] = usePathParam('portalId');
    if (isHideVsaEndpointsEnabled()) {
        return null;
    }
    if (isJiraAdmin) {
        return <VirtualServiceAgentHeaderForAdmin cloudId={cloudId} isJiraAdmin />;
    }
    // at this point all the users are help seekers and are logged in
    // we have no special checks for project admins, they continue to see the button in the header just like help seekers would
    if (portalId) {
        // this is different that portalId && projectId because we don't want to render the
        // button for a help seeker in portal if projectId is not present
        // if we do && it would get into the the help center header
        // we want it to explicitly render null if projectId is not present but portalId is present
        // better to not render anything in this case than to render the button that redirects to the help center conversation page
        if (projectId) {
            return (
                <VirtualServiceAgentHeaderForPortal
                    cloudId={cloudId}
                    portalId={portalId}
                    projectId={projectId}
                    isChatSideBarOpen={isChatSideBarOpen}
                    openChatSideBar={openChatSideBar}
                    closeChatSideBar={closeChatSideBar}
                    isProjectAdmin={isProjectAdmin}
                />
            );
        }
        return null;
    }
    return <VirtualServiceAgentHeaderForHelpCenter cloudId={cloudId} />;
};
export default VirtualServiceAgentHeaderDumbWrapper;
